import type { UseQueryOptions } from '@tanstack/react-query';

/**
 * Attach an `Idempotency-Key` header to a write so an offline replay never double-applies. Shared by
 * every typed hook module (family/admin/platform/library/cosmetics) — the single source for the header.
 */
export const idempotent = (key: string) => ({ headers: { 'Idempotency-Key': key } });

/**
 * Like {@link idempotent}, but opts the write OUT of the offline outbox (the mutator's `noOutbox`
 * config marker). For RESPONSE-BEARING creates (provision household, admin invites/reset links) whose
 * one-time token exists only in the response: queueing offline would resolve the mutation as
 * `undefined` (crashing `onSuccess`) while the later replay performs the write with its token never
 * shown. Offline must instead surface as a real network error the caller's `onError` handles. The
 * `Idempotency-Key` is kept so the server still dedupes manual retries.
 */
export const idempotentNoOutbox = (key: string) => ({
  headers: { 'Idempotency-Key': key },
  noOutbox: true,
});

/** `useQuery` options with `queryKey`/`queryFn` owned by the hook (callers tune the rest). */
export type QueryOpts<T> = Omit<UseQueryOptions<T, Error, T>, 'queryKey' | 'queryFn'>;
